<template>
	<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
		<div
			v-if="selected"
			class="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
		>
			<div class="flex flex-col lg:flex-row min-h-screen lg:min-h-0">
				<!-- Left Sidebar - Profile Card -->
				<aside
					class="w-full lg:w-2/5 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-8 lg:p-10"
				>
					<div class="flex flex-col h-full">
						<!-- Back Button -->
						<button
							@click="goBack"
							class="mb-8 text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center gap-2 transition-colors"
						>
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
							Back to list
						</button>

						<!-- Profile Image -->
						<div class="flex justify-center mb-8">
							<div class="relative">
								<img
									:src="
										selected.profileImage ||
										'https://via.placeholder.com/160?text=Profile'
									"
									alt="Profile"
									class="w-40 h-40 rounded-full object-cover border-4 border-white shadow-2xl ring-4 ring-blue-100"
								/>
								<div
									class="absolute bottom-0 right-2 w-10 h-10 bg-green-500 rounded-full border-4 border-white shadow-lg"
								></div>
							</div>
						</div>

						<!-- Name & Description -->
						<div class="text-center mb-10">
							<h1
								class="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight"
							>
								{{ selected.intermediaryNames }}
							</h1>
							<p
								class="mt-2 text-gray-600 text-sm max-w-xs mx-auto leading-relaxed"
							>
								{{
									selected.description ||
									'No description provided.'
								}}
							</p>
						</div>

						<!-- Key Info Grid -->
						<div class="grid grid-cols-2 gap-4 text-sm">
							<div
								class="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm border border-gray-100"
							>
								<p class="text-gray-500 font-medium">
									Category
								</p>
								<p class="font-semibold text-gray-900">
									{{ selected.category }}
								</p>
							</div>
							<div
								class="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm border border-gray-100"
							>
								<p class="text-gray-500 font-medium">
									License No.
								</p>
								<p class="font-semibold text-gray-900">
									{{ selected.licenseNumber }}
								</p>
							</div>
							<div
								class="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm border border-gray-100"
							>
								<p class="text-gray-500 font-medium">Status</p>
								<p
									class="font-semibold capitalize"
									:class="statusColor"
								>
									{{
										selected.status.toLowerCase() ||
										'unknown'
									}}
								</p>
							</div>
						</div>

						<!-- Additional Info -->
						<div class="mt-8 space-y-3 text-sm text-gray-700">
							<div
								class="flex justify-between py-2 border-b border-gray-200"
							>
								<span class="text-gray-500">Primary Email</span>
								<span class="font-medium text-gray-900">{{
									selected.primaryEmail
								}}</span>
							</div>
							<div
								class="flex justify-between py-2 border-b border-gray-200"
							>
								<span class="text-gray-500">KRA PIN</span>
								<span class="font-medium text-gray-900">{{
									selected.kraPin
								}}</span>
							</div>
							<div class="flex justify-between py-2">
								<span class="text-gray-500">Profile Type</span>
								<span
									class="font-medium text-gray-900 capitalize"
									>{{ selected.profileType }}</span
								>
							</div>
						</div>
					</div>
				</aside>

				<!-- Right Content Area -->
				<div class="w-full lg:w-3/5 p-4 sm:p-6 lg:p-12 bg-gray-50/50">
					<div class="space-y-6 sm:space-y-8 lg:space-y-10">
						<!-- Contacts -->
						<section>
							<h3
								class="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
									/>
								</svg>
								Contacts
							</h3>
							<div
								class="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5"
							>
								<ul
									class="space-y-2 text-sm sm:text-base text-gray-700"
								>
									<li
										v-for="(c, i) in selected.contacts"
										:key="i"
										class="flex items-start gap-2 break-all"
									>
										<span
											class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"
										></span>
										<span>{{ c }}</span>
									</li>
								</ul>
							</div>
						</section>

						<!-- Directors -->
						<section v-if="selected.kyc.directors">
							<h3
								class="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4"
							>
								Directors
							</h3>
							<div class="grid gap-4 sm:gap-5 md:grid-cols-2">
								<div
									v-for="(d, i) in selected.kyc.directors"
									:key="i"
									class="bg-white border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
								>
									<h4
										class="font-bold text-gray-900 text-base sm:text-lg mb-2 sm:mb-3"
									>
										{{ d.names }}
									</h4>
									<div
										class="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600"
									>
										<p>
											<span
												class="font-medium text-gray-800"
												>Salutation:</span
											>
											{{ d.salutation }}
										</p>
										<p>
											<span
												class="font-medium text-gray-800"
												>Country:</span
											>
											{{ d.country }}
										</p>
										<p>
											<span
												class="font-medium text-gray-800"
												>PIN:</span
											>
											{{ d.pin }}
										</p>
									</div>
								</div>
							</div>
						</section>

						<!-- Social Handles -->
						<section
							v-if="
								selected.kyc.socialMedia && hasAnySocialHandle
							"
						>
							<h3
								class="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-5 flex items-center gap-3"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
									/>
								</svg>
								Social Media
							</h3>

							<div
								class="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden"
							>
								<div class="divide-y divide-gray-100">
									<!-- Facebook -->
									<a
										v-if="facebook"
										:href="facebookUrl"
										target="_blank"
										rel="noopener noreferrer"
										class="flex items-center gap-4 p-4 sm:p-5 hover:bg-blue-50 transition-colors group"
									>
										<div
											class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors"
										>
											<i
												class="fab fa-facebook-f text-blue-600 text-lg sm:text-xl"
											></i>
										</div>
										<div class="flex-1 min-w-0">
											<p
												class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1"
											>
												Facebook
											</p>
											<p
												class="text-sm sm:text-base text-blue-600 font-medium truncate group-hover:text-blue-700"
											>
												{{ facebook }}
											</p>
										</div>
										<svg
											class="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
									</a>

									<!-- Twitter / X -->
									<a
										v-if="twitter"
										:href="twitterUrl"
										target="_blank"
										rel="noopener noreferrer"
										class="flex items-center gap-4 p-4 sm:p-5 hover:bg-gray-50 transition-colors group"
									>
										<div
											class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-800 transition-colors"
										>
											<svg
												fill="#fafafa"
												width="20px"
												height="20px"
												viewBox="0 0 33.88 33.88"
											>
												<path
													d="M30.414,10.031c0.014,0.297,0.021,0.595,0.021,0.897c0,9.187-6.992,19.779-19.779,19.779c-3.928,0-7.58-1.149-10.657-3.123 c0.546,0.063,1.099,0.095,1.658,0.095c3.26,0,6.254-1.107,8.632-2.974c-3.039-0.058-5.607-2.065-6.491-4.828 c0.424,0.082,0.858,0.125,1.308,0.125c0.635,0,1.248-0.084,1.83-0.244c-3.177-0.639-5.576-3.448-5.576-6.815 c0-0.029,0-0.058,0-0.087c0.939,0.521,2.01,0.833,3.15,0.869C2.646,12.48,1.419,10.35,1.419,7.938c0-1.274,0.343-2.467,0.94-3.495 c3.427,4.206,8.552,6.973,14.327,7.263c-0.117-0.509-0.18-1.038-0.18-1.584c0-3.838,3.112-6.949,6.953-6.949 c1.998,0,3.805,0.844,5.07,2.192c1.582-0.311,3.072-0.89,4.416-1.686c-0.521,1.624-1.621,2.986-3.057,3.844 c1.406-0.166,2.746-0.54,3.991-1.092C32.949,7.826,31.771,9.05,30.414,10.031z"
												/>
											</svg>
										</div>
										<div class="flex-1 min-w-0">
											<p
												class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1"
											>
												X (Twitter)
											</p>
											<p
												class="text-sm sm:text-base text-gray-800 font-medium truncate group-hover:text-gray-900"
											>
												{{ twitter }}
											</p>
										</div>
										<svg
											class="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
									</a>

									<!-- WhatsApp -->
									<a
										v-if="whatsapp"
										:href="whatsappUrl"
										target="_blank"
										rel="noopener noreferrer"
										class="flex items-center gap-4 p-4 sm:p-5 hover:bg-green-50 transition-colors group"
									>
										<div
											class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors"
										>
											<i
												class="fab fa-whatsapp text-green-600 text-2xl"
											></i>
										</div>
										<div class="flex-1 min-w-0">
											<p
												class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1"
											>
												WhatsApp
											</p>
											<p
												class="text-sm sm:text-base text-green-600 font-medium group-hover:text-green-700"
											>
												{{ formatPhone(whatsapp) }}
											</p>
										</div>
										<svg
											class="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
									</a>
								</div>
							</div>
						</section>

						<!-- Location -->
						<section>
							<h3
								class="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
									/>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
								Registered Address
							</h3>
							<div
								class="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6"
							>
								<div
									class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-700"
								>
									<div>
										<p
											class="text-gray-500 font-medium mb-1"
										>
											P.O. Box
										</p>
										<p class="font-semibold text-gray-900">
											{{ selected.kyc.pobox }}
										</p>
									</div>
									<div>
										<p
											class="text-gray-500 font-medium mb-1"
										>
											City
										</p>
										<p class="font-semibold text-gray-900">
											{{ selected.kyc.city }}
										</p>
									</div>
									<div>
										<p
											class="text-gray-500 font-medium mb-1"
										>
											Country
										</p>
										<p class="font-semibold text-gray-900">
											{{ selected.kyc.country }}
										</p>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'ViewIntermediary',
	computed: {
		...mapGetters('help', ['selectEntities']),

		intermediariesList() {
			return Array.isArray(this.selectEntities)
				? this.selectEntities
				: [];
		},

		selected() {
			const id = this.$route.params.id;
			if (!id) return null;
			return (
				this.intermediariesList.find((i) => {
					const candidate =
						i &&
						(i.id || i.intermediaryId || i.intermediarySystemRef);
					return (
						candidate != null && String(candidate) === String(id)
					);
				}) || null
			);
		},

		statusColor() {
			const s = this.selected.status.toLowerCase();
			return {
				'text-green-600': s === 'active' || s === 'approved',
				'text-red-600': s === 'suspended' || s === 'rejected',
				'text-yellow-600': s === 'pending',
				'text-gray-600': !s,
			};
		},

		facebook() {
			return this.cleanHandle(this.selected.kyc.socialMedia.facebook);
		},

		twitter() {
			return this.cleanHandle(this.selected.kyc.socialMedia.twitter);
		},

		whatsapp() {
			return this.cleanHandle(this.selected.kyc.socialMedia.whatsapp);
		},

		hasAnySocialHandle() {
			return !!(this.facebook || this.twitter || this.whatsapp);
		},

		facebookUrl() {
			if (!this.facebook) return '#';
			const handle = this.facebook.replace(/^@/, '');
			return `https://facebook.com/${handle}`;
		},

		twitterUrl() {
			if (!this.twitter) return '#';
			const handle = this.twitter.replace(/^@/, '');
			return `https://x.com/${handle}`;
		},

		whatsappUrl() {
			if (!this.whatsapp) return '#';
			const phone = this.whatsapp.replace(/\D/g, '');
			return `https://wa.me/${phone}`;
		},
	},

	methods: {
		cleanHandle(value) {
			if (!value) return '';
			return String(value).trim() || '';
		},

		formatPhone(phone) {
			if (!phone) return '';
			return phone.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');
		},

		goBack() {
			this.$router.go(-1);
		},
	},

	mounted() {
		console.log('===entity==', this.selected.kyc.socialMedia);
	},
};
</script>

<style scoped>
/* Custom scrollbar */
::-webkit-scrollbar {
	width: 6px;
}

::-webkit-scrollbar-track {
	background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}
</style>
